import axios from 'axios';
import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { SalesOrder, SimpleProduct } from '@msdyn365-commerce/retail-proxy';

import { UCConsentServiceNames } from '../userCentrics/const';
import { CookieService } from '../../shared/services/cookie-service/cookie-service';
import { ConsentGatedCookieNames } from '../../shared/services/cookie-service/cookie-service.const';
import { SPRII_ORDER_NO_COOKIE_EXPIRATION_DAYS } from './sprii.const';
import { TRAILING_SLASH_REGEX } from '../../const/regex';
import { getVariantDimensionValue } from '../../utils/msdynVariantDimensions';
import { getVariantConcatenatedId } from '../../utils/variantIds';

export class SpriiService {
    private readonly cookieService: CookieService;
    private readonly request: IRequestContext;

    constructor(request: IRequestContext) {
        this.request = request;
        this.cookieService = new CookieService(request);
    }

    public setSpriiOrderNo = async (orderNo: string): Promise<void> => {
        await this.cookieService.setConsentGatedCookie(UCConsentServiceNames.Sprii, ConsentGatedCookieNames.SpriiOrderNo, orderNo, {
            expirationDays: SPRII_ORDER_NO_COOKIE_EXPIRATION_DAYS
        });
    };

    private getSpriiOrderNo = (): string | undefined => {
        return this.cookieService.getRequestCookie<string>(ConsentGatedCookieNames.SpriiOrderNo);
    };

    private clearSpriiOrderNo = (): void => {
        this.cookieService.removeConsentGatedCookie(ConsentGatedCookieNames.SpriiOrderNo);
    };

    public trackCompletedOrder = async (salesOrder: SalesOrder, variants: SimpleProduct[]): Promise<void> => {
        if (typeof window === 'undefined') {
            return;
        }

        const orderNo = this.getSpriiOrderNo();
        if (!orderNo) {
            return;
        }

        await this.sendOrderCompleted(orderNo, salesOrder, variants);
        this.clearSpriiOrderNo();
    };

    private sendOrderCompleted = async (orderNo: string, salesOrder: SalesOrder, variants: SimpleProduct[]): Promise<void> => {
        if (salesOrder.TotalAmount === undefined) {
            throw new Error('Order does not have a total amount');
        }

        const items = variants
            .map(variant => {
                const line = salesOrder.SalesLines?.find(salesLine => salesLine.ProductId === variant.RecordId);
                if (!variant.ItemId || line?.Quantity === undefined || line.Price === undefined) {
                    return undefined;
                }

                const color = getVariantDimensionValue(variant.Dimensions, 'COLOR');
                const size = getVariantDimensionValue(variant.Dimensions, 'SIZE');
                const concatenatedId = getVariantConcatenatedId(variant.ItemId, color, size);

                if (!concatenatedId) {
                    return undefined;
                }

                return {
                    product_id: concatenatedId,
                    quantity: line.Quantity,
                    unitPrice: line.Price
                };
            })
            .filter(item => item !== undefined);

        if (!items || items.length === 0) {
            throw new Error('Sprii order does not have any valid items');
        }

        // Regex to strip trailing slashes, so that this works regardless
        // of whether there is one or not in the baseUrl
        const baseUrl = this.request.apiSettings.baseUrl.replace(TRAILING_SLASH_REGEX, '');

        const response = await axios.post(
            `${baseUrl}/Commerce/BSTrackSpriiOrder`,
            {
                data: {
                    // Enable test mode for doing dry runs
                    testmode: false,
                    orders: [
                        {
                            spriiOrderNumber: orderNo,
                            id: salesOrder.Id,
                            orderTotal: salesOrder.TotalAmount,
                            items
                        }
                    ]
                }
            },
            { headers: { oun: this.request.apiSettings.oun } }
        );

        if (response.data.StatusCode !== 200) {
            throw new Error(`[Sprii Service] Failed to track Sprii order: ${response.data.SpriiResponse.Message || 'Unknown error'}`);
        }
    };
}
